import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';

interface SubscriptionContextType {
  hasActiveSubscription: boolean;
  isLoading: boolean;
  refreshSubscription: () => Promise<void>;
  canAccessModule: (moduleId: number) => boolean;
  isAdmin: boolean;
  userRole: 'parent' | 'child' | null;
  isAdminChild: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { hasActiveSubscription, isLoading: subscriptionLoading, refetch, isInherited } = useSubscription();
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<'parent' | 'child' | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  
  // Admin child status comes from subscription hook
  const isAdminChild = isInherited;

  // Check user profile (admin status and role)
  useEffect(() => {
    const checkUserProfile = async () => {
      if (!user) {
        setIsAdmin(false);
        setUserRole(null);
        setProfileLoading(false);
        return;
      }

      try {
        // Check user role from profiles table
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Error checking user profile:', profileError);
          setUserRole(null);
        } else {
          setUserRole(profile?.role || null);
        }

        // Check admin status from user_roles table (secure)
        const { data: roles, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin');

        if (rolesError) {
          console.error('❌ Error checking user roles:', rolesError);
          setIsAdmin(false);
        } else {
          const hasAdmin = roles && roles.length > 0;
          console.log('🔐 Admin check result:', { userId: user.id, roles, hasAdmin });
          setIsAdmin(hasAdmin);
        }
      } catch (error) {
        console.error('Error in checkUserProfile:', error);
        setIsAdmin(false);
        setUserRole(null);
      } finally {
        setProfileLoading(false);
      }
    };

    checkUserProfile();
  }, [user]);

  const isLoading = subscriptionLoading || profileLoading;

  const canAccessModule = (moduleId: number): boolean => {
    // Admins can access all modules
    if (isAdmin) return true;
    
    // Admin children get free access to all modules
    if (isAdminChild) {
      console.log('🔓 [SubscriptionContext] Admin child accessing module', moduleId);
      return true;
    }
    
    // Module 1 is always free (demo module)
    if (moduleId === 1) return true;
    
    // Parents can only access module 1 (demo) - they should use the dashboard to monitor children
    if (userRole === 'parent') return false;
    
    // Children can access other modules if they have their own active subscription
    if (userRole === 'child') return hasActiveSubscription;
    
    // Default fallback for users without a defined role
    return hasActiveSubscription;
  };

  const refreshSubscription = async () => {
    if (user) {
      await refetch();
    }
  };

  const value: SubscriptionContextType = {
    hasActiveSubscription,
    isLoading,
    refreshSubscription,
    canAccessModule,
    isAdmin,
    userRole,
    isAdminChild,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};